// Phase 2: Role Definitions and Permissions
// Based on surfox_role_permissions_matrix.xlsx

export const PERMISSIONS = {
  // AI Settings
  VIEW_EDIT_AI_SETTINGS: 'view_edit_ai_settings',
  TOGGLE_AI_AFTER_HOURS_OVERRIDE: 'toggle_ai_after_hours_override',
  SET_ESCALATION_SCORE_THRESHOLD: 'set_escalation_score_threshold',
  CHOOSE_ESCALATION_METHOD: 'choose_escalation_method',
  MODIFY_AI_REPLY_MODE: 'modify_ai_reply_mode',
  
  // Instruction Hub
  EDIT_TONE_PERSONA_INDUSTRY_INSTRUCTIONS: 'edit_tone_persona_industry_instructions',
  REBUILD_AI_INSTRUCTION_BUNDLE: 'rebuild_ai_instruction_bundle',
  VIEW_AI_BUNDLE_PREVIEW: 'view_ai_bundle_preview',
  UPLOAD_KNOWLEDGE_BASE_DOCS: 'upload_knowledge_base_docs',
  DELETE_KB_DOCS: 'delete_kb_docs',
  
  // System Tools
  EXPORT_PLATFORM_CONFIG_JSON: 'export_platform_config_json',
  IMPORT_PLATFORM_CONFIG: 'import_platform_config',
  VIEW_EXPORT_MESSAGE_LOGS: 'view_export_message_logs',
  EXPORT_ESCALATION_LOG_HOT_LEADS: 'export_escalation_log_hot_leads',
  
  // Settings
  VIEW_EDIT_COMPANY_INFO: 'view_edit_company_info',
  SET_OFFICE_HOURS_DAYS: 'set_office_hours_days',
  CONFIGURE_ACCEPTABLE_LEAD_STATUSES: 'configure_acceptable_lead_statuses',
  ACCESS_ALL_SETTINGS_TABS: 'access_all_settings_tabs',
  
  // Phone Number Management
  VIEW_PHONE_NUMBERS: 'view_phone_numbers',
  MANAGE_PHONE_NUMBERS: 'manage_phone_numbers',
  ASSIGN_PHONE_NUMBERS: 'assign_phone_numbers',

  // Phone Number Port-In
  INITIATE_PORT_REQUEST: 'initiate_port_request',
  VIEW_PORT_REQUESTS: 'view_port_requests',
  CANCEL_PORT_REQUEST: 'cancel_port_request',
  
  // Team Management
  VIEW_TEAM_MEMBERS: 'view_team_members',
  INVITE_REMOVE_USERS: 'invite_remove_users',
  VIEW_TEAM_ROLES: 'view_team_roles',
  CHANGE_USER_ROLES: 'change_user_roles',
  ASSIGN_TENANT_METADATA: 'assign_tenant_metadata',
  MANAGE_USERS: 'manage_users',
  
  // Partner-specific permissions
  VIEW_PARTNER_NETWORK: 'view_partner_network',
  MANAGE_PARTNER_LOCATIONS: 'manage_partner_locations',
  CREATE_NEW_LOCATIONS: 'create_new_locations',
  VIEW_NETWORK_ANALYTICS: 'view_network_analytics',
  
  // Messaging
  VIEW_ALL_MESSAGES: 'view_all_messages',
  SEARCH_FILTER_MESSAGES: 'search_filter_messages',
  VIEW_MESSAGE_LOGS_QUEUE_STATUS: 'view_message_logs_queue_status',
  MANUALLY_MARK_MESSAGE_AS_ESCALATED: 'manually_mark_message_as_escalated',
  TRIGGER_MANUAL_AI_REPLY: 'trigger_manual_ai_reply',
  EDIT_SMS_TEMPLATES: 'edit_sms_templates',
  
  // Lead Management
  VIEW_LEADS: 'view_leads',
  ADD_EDIT_LEAD_MANUALLY: 'add_edit_lead_manually',
  BULK_IMPORT_LEADS: 'bulk_import_leads',
  EXPORT_LEADS_TO_CSV: 'export_leads_to_csv',
  OVERRIDE_LEAD_STATUS: 'override_lead_status',
  TAG_LEAD_AS_HOT_ESCALATED: 'tag_lead_as_hot_escalated',
  ADD_MANUAL_NOTES_OR_COMMENTS: 'add_manual_notes_or_comments',
  VIEW_ESCALATION_SUMMARY: 'view_escalation_summary',
  
  // AI Control Room
  VIEW_FUNNEL_STATS: 'view_funnel_stats',
  FILTER_BY_CAMPAIGN_OR_DATE: 'filter_by_campaign_or_date',
  VIEW_AI_EFFICIENCY_BREAKDOWN: 'view_ai_efficiency_breakdown',
  VIEW_ESCALATION_SUMMARIES: 'view_escalation_summaries',
  EXPORT_PERFORMANCE_STATS: 'export_performance_stats',
  
  // Billing
  ACCESS_STRIPE_BILLING_PORTAL: 'access_stripe_billing_portal',
  VIEW_PLAN_QUOTA_USAGE: 'view_plan_quota_usage',
  UPGRADE_OR_CANCEL_SUBSCRIPTION: 'upgrade_or_cancel_subscription',
  VIEW_BILLING_HISTORY: 'view_billing_history',
  VIEW_SYSTEM_STATUS_ALERTS: 'view_system_status_alerts',
  
  // Demo Logic
  ACCESS_LIVE_TENANT_DATA: 'access_live_tenant_data',
  USE_DEMO_ONLY_TEST_DATA: 'use_demo_only_test_data',
  RESET_ACCOUNT_STATE: 'reset_account_state'
};

export const ROLE_PERMISSIONS = {
  global_admin: [
    // All permissions (platform owner)
    PERMISSIONS.VIEW_EDIT_AI_SETTINGS,
    PERMISSIONS.TOGGLE_AI_AFTER_HOURS_OVERRIDE,
    PERMISSIONS.SET_ESCALATION_SCORE_THRESHOLD,
    PERMISSIONS.CHOOSE_ESCALATION_METHOD,
    PERMISSIONS.MODIFY_AI_REPLY_MODE,
    PERMISSIONS.EDIT_TONE_PERSONA_INDUSTRY_INSTRUCTIONS,
    PERMISSIONS.REBUILD_AI_INSTRUCTION_BUNDLE,
    PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW,
    PERMISSIONS.UPLOAD_KNOWLEDGE_BASE_DOCS,
    PERMISSIONS.DELETE_KB_DOCS,
    PERMISSIONS.EXPORT_PLATFORM_CONFIG_JSON,
    PERMISSIONS.IMPORT_PLATFORM_CONFIG,
    PERMISSIONS.VIEW_EXPORT_MESSAGE_LOGS,
    PERMISSIONS.EXPORT_ESCALATION_LOG_HOT_LEADS,
    PERMISSIONS.VIEW_EDIT_COMPANY_INFO,
    PERMISSIONS.SET_OFFICE_HOURS_DAYS,
    PERMISSIONS.CONFIGURE_ACCEPTABLE_LEAD_STATUSES,
    PERMISSIONS.ACCESS_ALL_SETTINGS_TABS,
    PERMISSIONS.VIEW_PHONE_NUMBERS,
    PERMISSIONS.MANAGE_PHONE_NUMBERS,
    PERMISSIONS.ASSIGN_PHONE_NUMBERS,
    PERMISSIONS.INITIATE_PORT_REQUEST,
    PERMISSIONS.VIEW_PORT_REQUESTS,
    PERMISSIONS.CANCEL_PORT_REQUEST,
    PERMISSIONS.VIEW_TEAM_MEMBERS,
    PERMISSIONS.INVITE_REMOVE_USERS,
    PERMISSIONS.VIEW_TEAM_ROLES,
    PERMISSIONS.CHANGE_USER_ROLES,
    PERMISSIONS.ASSIGN_TENANT_METADATA,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_PARTNER_NETWORK,
    PERMISSIONS.MANAGE_PARTNER_LOCATIONS,
    PERMISSIONS.CREATE_NEW_LOCATIONS,
    PERMISSIONS.VIEW_NETWORK_ANALYTICS,
    PERMISSIONS.VIEW_ALL_MESSAGES,
    PERMISSIONS.SEARCH_FILTER_MESSAGES,
    PERMISSIONS.VIEW_MESSAGE_LOGS_QUEUE_STATUS,
    PERMISSIONS.MANUALLY_MARK_MESSAGE_AS_ESCALATED,
    PERMISSIONS.TRIGGER_MANUAL_AI_REPLY,
    PERMISSIONS.EDIT_SMS_TEMPLATES,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.ADD_EDIT_LEAD_MANUALLY,
    PERMISSIONS.BULK_IMPORT_LEADS,
    PERMISSIONS.EXPORT_LEADS_TO_CSV,
    PERMISSIONS.OVERRIDE_LEAD_STATUS,
    PERMISSIONS.TAG_LEAD_AS_HOT_ESCALATED,
    PERMISSIONS.ADD_MANUAL_NOTES_OR_COMMENTS,
    PERMISSIONS.VIEW_ESCALATION_SUMMARY,
    PERMISSIONS.VIEW_FUNNEL_STATS,
    PERMISSIONS.FILTER_BY_CAMPAIGN_OR_DATE,
    PERMISSIONS.VIEW_AI_EFFICIENCY_BREAKDOWN,
    PERMISSIONS.VIEW_ESCALATION_SUMMARIES,
    PERMISSIONS.EXPORT_PERFORMANCE_STATS,
    PERMISSIONS.ACCESS_STRIPE_BILLING_PORTAL,
    PERMISSIONS.VIEW_PLAN_QUOTA_USAGE,
    PERMISSIONS.UPGRADE_OR_CANCEL_SUBSCRIPTION,
    PERMISSIONS.VIEW_BILLING_HISTORY,
    PERMISSIONS.VIEW_SYSTEM_STATUS_ALERTS,
    PERMISSIONS.ACCESS_LIVE_TENANT_DATA,
    PERMISSIONS.USE_DEMO_ONLY_TEST_DATA,
    PERMISSIONS.RESET_ACCOUNT_STATE
  ],
  
  partner_admin: [
    // Partner network management with business_admin-level control across multiple locations
    PERMISSIONS.VIEW_EDIT_AI_SETTINGS,
    PERMISSIONS.TOGGLE_AI_AFTER_HOURS_OVERRIDE,
    PERMISSIONS.SET_ESCALATION_SCORE_THRESHOLD,
    PERMISSIONS.CHOOSE_ESCALATION_METHOD,
    PERMISSIONS.MODIFY_AI_REPLY_MODE,
    PERMISSIONS.EDIT_TONE_PERSONA_INDUSTRY_INSTRUCTIONS,
    PERMISSIONS.REBUILD_AI_INSTRUCTION_BUNDLE,
    PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW,
    PERMISSIONS.UPLOAD_KNOWLEDGE_BASE_DOCS,
    PERMISSIONS.DELETE_KB_DOCS,
    PERMISSIONS.EXPORT_PLATFORM_CONFIG_JSON,
    PERMISSIONS.IMPORT_PLATFORM_CONFIG,
    PERMISSIONS.VIEW_EXPORT_MESSAGE_LOGS,
    PERMISSIONS.EXPORT_ESCALATION_LOG_HOT_LEADS,
    PERMISSIONS.VIEW_EDIT_COMPANY_INFO,
    PERMISSIONS.SET_OFFICE_HOURS_DAYS,
    PERMISSIONS.CONFIGURE_ACCEPTABLE_LEAD_STATUSES,
    PERMISSIONS.ACCESS_ALL_SETTINGS_TABS,
    PERMISSIONS.VIEW_PHONE_NUMBERS,
    PERMISSIONS.MANAGE_PHONE_NUMBERS,
    PERMISSIONS.ASSIGN_PHONE_NUMBERS,
    PERMISSIONS.INITIATE_PORT_REQUEST,
    PERMISSIONS.VIEW_PORT_REQUESTS,
    PERMISSIONS.CANCEL_PORT_REQUEST,
    PERMISSIONS.VIEW_TEAM_MEMBERS,
    PERMISSIONS.INVITE_REMOVE_USERS,
    PERMISSIONS.VIEW_TEAM_ROLES,
    PERMISSIONS.CHANGE_USER_ROLES,
    PERMISSIONS.ASSIGN_TENANT_METADATA,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_PARTNER_NETWORK,
    PERMISSIONS.MANAGE_PARTNER_LOCATIONS,
    PERMISSIONS.CREATE_NEW_LOCATIONS,
    PERMISSIONS.VIEW_NETWORK_ANALYTICS,
    PERMISSIONS.VIEW_ALL_MESSAGES,
    PERMISSIONS.SEARCH_FILTER_MESSAGES,
    PERMISSIONS.VIEW_MESSAGE_LOGS_QUEUE_STATUS,
    PERMISSIONS.MANUALLY_MARK_MESSAGE_AS_ESCALATED,
    PERMISSIONS.TRIGGER_MANUAL_AI_REPLY,
    PERMISSIONS.EDIT_SMS_TEMPLATES,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.ADD_EDIT_LEAD_MANUALLY,
    PERMISSIONS.BULK_IMPORT_LEADS,
    PERMISSIONS.EXPORT_LEADS_TO_CSV,
    PERMISSIONS.OVERRIDE_LEAD_STATUS,
    PERMISSIONS.TAG_LEAD_AS_HOT_ESCALATED,
    PERMISSIONS.ADD_MANUAL_NOTES_OR_COMMENTS,
    PERMISSIONS.VIEW_ESCALATION_SUMMARY,
    PERMISSIONS.VIEW_FUNNEL_STATS,
    PERMISSIONS.FILTER_BY_CAMPAIGN_OR_DATE,
    PERMISSIONS.VIEW_AI_EFFICIENCY_BREAKDOWN,
    PERMISSIONS.VIEW_ESCALATION_SUMMARIES,
    PERMISSIONS.EXPORT_PERFORMANCE_STATS,
    PERMISSIONS.ACCESS_STRIPE_BILLING_PORTAL,
    PERMISSIONS.VIEW_PLAN_QUOTA_USAGE,
    PERMISSIONS.UPGRADE_OR_CANCEL_SUBSCRIPTION,
    PERMISSIONS.VIEW_BILLING_HISTORY,
    PERMISSIONS.VIEW_SYSTEM_STATUS_ALERTS,
    PERMISSIONS.ACCESS_LIVE_TENANT_DATA
  ],

  co_admin: [
    // Full parity with partner_admin - shares same tenant, peer-level access
    PERMISSIONS.VIEW_EDIT_AI_SETTINGS,
    PERMISSIONS.TOGGLE_AI_AFTER_HOURS_OVERRIDE,
    PERMISSIONS.SET_ESCALATION_SCORE_THRESHOLD,
    PERMISSIONS.CHOOSE_ESCALATION_METHOD,
    PERMISSIONS.MODIFY_AI_REPLY_MODE,
    PERMISSIONS.EDIT_TONE_PERSONA_INDUSTRY_INSTRUCTIONS,
    PERMISSIONS.REBUILD_AI_INSTRUCTION_BUNDLE,
    PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW,
    PERMISSIONS.UPLOAD_KNOWLEDGE_BASE_DOCS,
    PERMISSIONS.DELETE_KB_DOCS,
    PERMISSIONS.EXPORT_PLATFORM_CONFIG_JSON,
    PERMISSIONS.IMPORT_PLATFORM_CONFIG,
    PERMISSIONS.VIEW_EXPORT_MESSAGE_LOGS,
    PERMISSIONS.EXPORT_ESCALATION_LOG_HOT_LEADS,
    PERMISSIONS.VIEW_EDIT_COMPANY_INFO,
    PERMISSIONS.SET_OFFICE_HOURS_DAYS,
    PERMISSIONS.CONFIGURE_ACCEPTABLE_LEAD_STATUSES,
    PERMISSIONS.ACCESS_ALL_SETTINGS_TABS,
    PERMISSIONS.VIEW_PHONE_NUMBERS,
    PERMISSIONS.MANAGE_PHONE_NUMBERS,
    PERMISSIONS.ASSIGN_PHONE_NUMBERS,
    PERMISSIONS.INITIATE_PORT_REQUEST,
    PERMISSIONS.VIEW_PORT_REQUESTS,
    PERMISSIONS.CANCEL_PORT_REQUEST,
    PERMISSIONS.VIEW_TEAM_MEMBERS,
    PERMISSIONS.INVITE_REMOVE_USERS,
    PERMISSIONS.VIEW_TEAM_ROLES,
    PERMISSIONS.CHANGE_USER_ROLES,
    PERMISSIONS.ASSIGN_TENANT_METADATA,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_PARTNER_NETWORK,
    PERMISSIONS.MANAGE_PARTNER_LOCATIONS,
    PERMISSIONS.CREATE_NEW_LOCATIONS,
    PERMISSIONS.VIEW_NETWORK_ANALYTICS,
    PERMISSIONS.VIEW_ALL_MESSAGES,
    PERMISSIONS.SEARCH_FILTER_MESSAGES,
    PERMISSIONS.VIEW_MESSAGE_LOGS_QUEUE_STATUS,
    PERMISSIONS.MANUALLY_MARK_MESSAGE_AS_ESCALATED,
    PERMISSIONS.TRIGGER_MANUAL_AI_REPLY,
    PERMISSIONS.EDIT_SMS_TEMPLATES,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.ADD_EDIT_LEAD_MANUALLY,
    PERMISSIONS.BULK_IMPORT_LEADS,
    PERMISSIONS.EXPORT_LEADS_TO_CSV,
    PERMISSIONS.OVERRIDE_LEAD_STATUS,
    PERMISSIONS.TAG_LEAD_AS_HOT_ESCALATED,
    PERMISSIONS.ADD_MANUAL_NOTES_OR_COMMENTS,
    PERMISSIONS.VIEW_ESCALATION_SUMMARY,
    PERMISSIONS.VIEW_FUNNEL_STATS,
    PERMISSIONS.FILTER_BY_CAMPAIGN_OR_DATE,
    PERMISSIONS.VIEW_AI_EFFICIENCY_BREAKDOWN,
    PERMISSIONS.VIEW_ESCALATION_SUMMARIES,
    PERMISSIONS.EXPORT_PERFORMANCE_STATS,
    PERMISSIONS.ACCESS_STRIPE_BILLING_PORTAL,
    PERMISSIONS.VIEW_PLAN_QUOTA_USAGE,
    PERMISSIONS.UPGRADE_OR_CANCEL_SUBSCRIPTION,
    PERMISSIONS.VIEW_BILLING_HISTORY,
    PERMISSIONS.VIEW_SYSTEM_STATUS_ALERTS,
    PERMISSIONS.ACCESS_LIVE_TENANT_DATA
  ],
  
  business_admin: [
    // Tenant owner permissions (cannot access demo logic or partner features)
    PERMISSIONS.VIEW_EDIT_AI_SETTINGS,
    PERMISSIONS.TOGGLE_AI_AFTER_HOURS_OVERRIDE,
    PERMISSIONS.SET_ESCALATION_SCORE_THRESHOLD,
    PERMISSIONS.CHOOSE_ESCALATION_METHOD,
    PERMISSIONS.MODIFY_AI_REPLY_MODE,
    PERMISSIONS.EDIT_TONE_PERSONA_INDUSTRY_INSTRUCTIONS,
    PERMISSIONS.REBUILD_AI_INSTRUCTION_BUNDLE,
    PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW,
    PERMISSIONS.UPLOAD_KNOWLEDGE_BASE_DOCS,
    PERMISSIONS.DELETE_KB_DOCS,
    PERMISSIONS.EXPORT_PLATFORM_CONFIG_JSON,
    PERMISSIONS.IMPORT_PLATFORM_CONFIG,
    PERMISSIONS.VIEW_EXPORT_MESSAGE_LOGS,
    PERMISSIONS.EXPORT_ESCALATION_LOG_HOT_LEADS,
    PERMISSIONS.VIEW_EDIT_COMPANY_INFO,
    PERMISSIONS.SET_OFFICE_HOURS_DAYS,
    PERMISSIONS.CONFIGURE_ACCEPTABLE_LEAD_STATUSES,
    PERMISSIONS.ACCESS_ALL_SETTINGS_TABS,
    PERMISSIONS.VIEW_PHONE_NUMBERS,
    PERMISSIONS.MANAGE_PHONE_NUMBERS,
    PERMISSIONS.ASSIGN_PHONE_NUMBERS,
    PERMISSIONS.INITIATE_PORT_REQUEST,
    PERMISSIONS.VIEW_PORT_REQUESTS,
    PERMISSIONS.CANCEL_PORT_REQUEST,
    PERMISSIONS.VIEW_TEAM_MEMBERS,
    PERMISSIONS.INVITE_REMOVE_USERS,
    PERMISSIONS.VIEW_TEAM_ROLES,
    PERMISSIONS.CHANGE_USER_ROLES,
    PERMISSIONS.ASSIGN_TENANT_METADATA,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_ALL_MESSAGES,
    PERMISSIONS.SEARCH_FILTER_MESSAGES,
    PERMISSIONS.VIEW_MESSAGE_LOGS_QUEUE_STATUS,
    PERMISSIONS.MANUALLY_MARK_MESSAGE_AS_ESCALATED,
    PERMISSIONS.TRIGGER_MANUAL_AI_REPLY,
    PERMISSIONS.EDIT_SMS_TEMPLATES,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.ADD_EDIT_LEAD_MANUALLY,
    PERMISSIONS.BULK_IMPORT_LEADS,
    PERMISSIONS.EXPORT_LEADS_TO_CSV,
    PERMISSIONS.OVERRIDE_LEAD_STATUS,
    PERMISSIONS.TAG_LEAD_AS_HOT_ESCALATED,
    PERMISSIONS.ADD_MANUAL_NOTES_OR_COMMENTS,
    PERMISSIONS.VIEW_ESCALATION_SUMMARY,
    PERMISSIONS.VIEW_FUNNEL_STATS,
    PERMISSIONS.FILTER_BY_CAMPAIGN_OR_DATE,
    PERMISSIONS.VIEW_AI_EFFICIENCY_BREAKDOWN,
    PERMISSIONS.VIEW_ESCALATION_SUMMARIES,
    PERMISSIONS.EXPORT_PERFORMANCE_STATS,
    PERMISSIONS.ACCESS_STRIPE_BILLING_PORTAL,
    PERMISSIONS.VIEW_PLAN_QUOTA_USAGE,
    PERMISSIONS.UPGRADE_OR_CANCEL_SUBSCRIPTION,
    PERMISSIONS.VIEW_BILLING_HISTORY,
    PERMISSIONS.VIEW_SYSTEM_STATUS_ALERTS,
    PERMISSIONS.ACCESS_LIVE_TENANT_DATA
  ],
  
  user: [
    // Limited employee permissions
    PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW,
    PERMISSIONS.VIEW_PHONE_NUMBERS,
    PERMISSIONS.VIEW_PORT_REQUESTS,
    PERMISSIONS.VIEW_TEAM_MEMBERS,
    PERMISSIONS.VIEW_ALL_MESSAGES,
    PERMISSIONS.SEARCH_FILTER_MESSAGES,
    PERMISSIONS.VIEW_MESSAGE_LOGS_QUEUE_STATUS,
    PERMISSIONS.TRIGGER_MANUAL_AI_REPLY,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.ADD_EDIT_LEAD_MANUALLY,
    PERMISSIONS.BULK_IMPORT_LEADS,
    PERMISSIONS.OVERRIDE_LEAD_STATUS,
    PERMISSIONS.TAG_LEAD_AS_HOT_ESCALATED,
    PERMISSIONS.ADD_MANUAL_NOTES_OR_COMMENTS,
    PERMISSIONS.VIEW_ESCALATION_SUMMARY,
    PERMISSIONS.VIEW_FUNNEL_STATS,
    PERMISSIONS.FILTER_BY_CAMPAIGN_OR_DATE,
    PERMISSIONS.VIEW_AI_EFFICIENCY_BREAKDOWN,
    PERMISSIONS.VIEW_ESCALATION_SUMMARIES,
    PERMISSIONS.VIEW_PLAN_QUOTA_USAGE,
    PERMISSIONS.VIEW_SYSTEM_STATUS_ALERTS,
    PERMISSIONS.ACCESS_LIVE_TENANT_DATA
  ]
};

// Helper function to check if a role has a specific permission
export const hasPermission = (role, permission) => {
  return ROLE_PERMISSIONS[role]?.includes(permission) || false;
};

// Helper function to get all permissions for a role
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || [];
};

// Role metadata for display purposes
export const ROLE_METADATA = {
  global_admin: {
    name: 'Global Admin',
    description: 'Platform owner with full access to all features',
    level: 4
  },
  partner_admin: {
    name: 'Partner Admin',
    description: 'Partner with access to multiple business locations under their network',
    level: 3
  },
  co_admin: {
    name: 'Co-Admin',
    description: 'Co-administrator sharing the same tenant with full partner-level access',
    level: 3
  },
  business_admin: {
    name: 'Business Admin',
    description: 'Business owner with full access to their organization',
    level: 2
  },
  user: {
    name: 'User',
    description: 'Team member with limited permissions',
    level: 1
  }
};